import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Plus, RefreshCw, Trash2, List } from "lucide-react";

export default function ManageModelsDialog({ open, onOpenChange, provider, onSaved }) {
  const [models, setModels] = useState([]);
  const [newModel, setNewModel] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState([]);

  useEffect(() => {
    if (open && provider) {
      setModels(provider.models || []);
      setAvailable([]);
      setNewModel("");
    }
  }, [open, provider]);

  async function refreshFromProvider() {
    setRefreshing(true);
    try {
      const { data } = await api.get(
        `/providers/${encodeURIComponent(provider.id)}/models?refresh=true`
      );
      const list = (data?.data || data || []).map((m) => (typeof m === "string" ? m : m.id))
        .filter(Boolean);
      setAvailable(list);
      toast.success(`${list.length} modelos disponibles en el proveedor`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  }

  function add(id) {
    const v = id.trim();
    if (!v) return;
    if (models.includes(v)) return;
    setModels((m) => [...m, v]);
    setNewModel("");
  }
  function remove(id) {
    setModels((m) => m.filter((x) => x !== id));
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/providers/${encodeURIComponent(provider.id)}/models`,
        { models }
      );
      toast.success("Lista de modelos guardada");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.providers.modelsDialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <List className="h-4 w-4" />
            Modelos de {provider.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Modelos que expondrás a tus apps para este proveedor. Puedes cargar
            los que ofrece la API y/o añadir manuales.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Button
            data-testid={TID.providers.modelsRefresh}
            variant="secondary" onClick={refreshFromProvider} disabled={refreshing}
            className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 h-8"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Cargar desde el proveedor
          </Button>
          <span className="text-[11px] text-zinc-500 font-mono">
            {available.length ? `${available.length} disponibles` : ""}
          </span>
        </div>

        {available.length > 0 && (
          <div className="rounded-md border border-zinc-800 bg-black/40 p-3 max-h-40 overflow-y-auto space-y-1">
            {available.map((m) => (
              <div key={m} className="flex items-center justify-between text-xs">
                <span className="font-mono text-zinc-300 truncate">{m}</span>
                <button
                  onClick={() => add(m)}
                  disabled={models.includes(m)}
                  className="text-[11px] text-emerald-400 hover:text-white disabled:opacity-40 shrink-0 pl-2"
                >
                  {models.includes(m) ? "añadido" : "añadir"}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-md border border-zinc-800 bg-zinc-950">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2">
            <Input
              data-testid={TID.providers.modelsAddInput}
              value={newModel} onChange={(e) => setNewModel(e.target.value)}
              placeholder="Añadir modelo manualmente (ej. openai/gpt-5.2)"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm h-8"
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); add(newModel); }
              }}
            />
            <Button
              data-testid={TID.providers.modelsAdd}
              onClick={() => add(newModel)}
              className="h-8 bg-white text-black hover:bg-zinc-200"
              size="sm"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Añadir
            </Button>
          </div>

          <div
            data-testid={TID.providers.modelsList}
            className="max-h-72 overflow-y-auto divide-y divide-zinc-900"
          >
            {models.length === 0 ? (
              <div className="p-6 text-center text-xs text-zinc-500">
                Aún no hay modelos. Añade uno manual o carga desde el proveedor.
              </div>
            ) : (
              models.map((m) => (
                <div key={m} className="flex items-center justify-between px-3 py-2">
                  <span className="font-mono text-xs text-zinc-200 truncate">{m}</span>
                  <button
                    onClick={() => remove(m)}
                    className="h-7 w-7 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900"
                    title="Quitar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}
            className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800">
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving}
            className="bg-white text-black hover:bg-zinc-200">
            {saving ? "Guardando…" : "Guardar lista"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
